import type { CoreError } from '@ingenico/api-sdk-core/common';
import type {
  EnumCallStatusApi,
  SearchFilterApi,
  SearchRequestSortApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import { searchFilterFilterType, searchOperator } from '@ingenico/api-sdk-device-management-private-v2';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import type { GridSortModel } from '@mui/x-data-grid-pro';
import { toString } from 'lodash-es';
import type { TFunction } from 'react-i18next';

export interface TransformFiltersParams {
  readonly selectedQuickFilterValue?: EnumCallStatusApi[];
  readonly searchQuery?: string;
  readonly startDate: number;
  readonly endDate: number;
}

export function getField(field: string): string {
  const fieldMappings: Record<string, string> = {
    application: 'deviceCall.metadata.logEmmiterType',
    status: 'deviceCall.status',
    reference: 'deviceCall.device.reference',
    serialNumber: 'deviceCall.device.serialNumber',
    callDateTime: 'deviceCall.metadata.date',
    callDuration: 'deviceCall.metadata.duration',
    callId: 'deviceCall.metadata.callingApplicationPackageId',
    statusSummary: 'deviceCall.metadata.reason',
  };

  return fieldMappings[field] || `deviceCall.${field}`;
}

export const defaultLogicalDeviceHistorySort: GridSortModel = [
  {
    field: 'callDateTime',
    sort: 'desc',
  },
];
export function transformSortModelForRequest(gridSort: GridSortModel): SearchRequestSortApi {
  const [currentSort] = gridSort;
  return currentSort?.sort
    ? {
        field: getField(currentSort.field),
        order: currentSort.sort === 'asc' ? 'ASC' : 'DESC',
      }
    : transformSortModelForRequest(defaultLogicalDeviceHistorySort);
}

export function transformFiltersToRequestCall({
  startDate,
  endDate,
  selectedQuickFilterValue,
  searchQuery,
}: TransformFiltersParams): SearchFilterApi[] {
  const startDateFilter = startDate
    ? [
        {
          filterType: searchFilterFilterType.string,
          name: getField('callDateTime'),
          operator: searchOperator.greaterThan,
          value: new Date(startDate).toISOString(),
        },
      ]
    : [];
  const endDateFilter = endDate
    ? [
        {
          filterType: searchFilterFilterType.string,
          name: getField('callDateTime'),
          operator: searchOperator.lowerThan,
          value: new Date(endDate).toISOString(),
        },
      ]
    : [];

  const quickFilter =
    selectedQuickFilterValue && selectedQuickFilterValue.length > 0
      ? [
          {
            filterType: searchFilterFilterType.array,
            name: getField('status'),
            operator: searchOperator.in,
            value: JSON.stringify([toString(selectedQuickFilterValue?.[0])]),
          },
        ]
      : [];

  const searchFilter = searchQuery
    ? [
        {
          filterType: searchFilterFilterType.fulltext,
          name: '',
          operator: searchOperator.equals,
          value: `*${searchQuery}*`,
        },
      ]
    : [];

  return [...quickFilter, ...searchFilter, ...startDateFilter, ...endDateFilter] as SearchFilterApi[];
}

export function getLogicalDeviceHistoryTabErrorMsg(t: TFunction, error?: CoreError | null): string {
  switch (error?.status) {
    case 404: {
      return t('error.default.deviceCallLogsDataNotFound', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE });
    }
    default: {
      return t('error.default.fetchDeviceCallLogsData', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE });
    }
  }
}
